import { useRef } from 'react'
import { User, Session } from '../types/auth'
import { supabase } from '../lib/supabaseClient'
import { withRetry } from '../utils/dbRetry'

// TypeScript declaration for Google Ads conversion tracking
declare global {
  interface Window {
    trackPurchaseConversion?: (transactionId?: string) => void
  }
}

interface UseSubscriptionParams {
  user: User | null
  session: Session | null
  onSubscriptionChange: (value: boolean) => void
  onTrialChange: (isActive: boolean, endDate: string | null) => void
  onLoadingChange: (checking: boolean, loading: boolean) => void
  updateTrialInDatabase: (email: string, isActive: boolean, endDate: string | null) => Promise<void>
}

interface UseSubscriptionReturn {
  fetchSubscription: (email: string | undefined) => Promise<void>
  pollingCleanup: () => void
}

export function useSubscription({
  user,
  session,
  onSubscriptionChange,
  onTrialChange,
  onLoadingChange,
  updateTrialInDatabase
}: UseSubscriptionParams): UseSubscriptionReturn {
  // Ref to track and cleanup polling
  const pollingCleanupRef = useRef<(() => void) | null>(null)

  // Ref to prevent duplicate subscription fetches
  const fetchingSubscriptionRef = useRef(false)

  // Helper function to track conversion when subscription is newly activated
  const trackConversionIfNew = (email: string, isNewSubscription: boolean = false) => {
    if (isNewSubscription && typeof window !== 'undefined' && window.trackPurchaseConversion) {
      window.trackPurchaseConversion(email)
    }
  }

  // Start polling with proper cleanup to prevent memory leaks
  function startPolling(email: string) {
    let attempts = 0
    const maxAttempts = 10
    const pollingInterval = 2000 // 2 seconds
    let disposed = false
    let timeoutId: ReturnType<typeof setTimeout> | null = null

    const poll = async () => {
      if (disposed) return

      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('is_subscribed, is_trial, trial_end_date')
          .eq('email', email.toLowerCase())
          .single()

        if (disposed) return // Check again after async operation

        if (!error && data) {
          if (data.is_subscribed) {
            // This is definitely a NEW subscription since we're polling for it
            const isNewSubscription = true

            onSubscriptionChange(true)

            // Track conversion for new subscription found during polling
            trackConversionIfNew(email, isNewSubscription)

            onLoadingChange(false, false)

            // Mark disposed and clear pending timer
            disposed = true
            if (timeoutId) clearTimeout(timeoutId)
            return // Stop polling
          }

          // Check for active trial during polling with expiration handling
          if (data.is_trial && data.trial_end_date) {
            const trialEnd = new Date(data.trial_end_date)
            const now = new Date()

            if (trialEnd > now) {
              // Active trial
              onTrialChange(true, data.trial_end_date)
              onLoadingChange(false, false)

              // Mark disposed and clear pending timer
              disposed = true
              if (timeoutId) clearTimeout(timeoutId)
              return // Stop polling
            } else {
              // Trial expired during polling
              await updateTrialInDatabase(email, false, null)
            }
          }
        }
      } catch (err) {
        // Silent error handling
      }

      attempts++

      if (attempts < maxAttempts && !disposed) {
        // Schedule next poll
        timeoutId = setTimeout(poll, pollingInterval)
      } else {
        if (!disposed) {
          onLoadingChange(false, false)
        }
      }
    }

    // Start first poll after a short delay
    timeoutId = setTimeout(poll, pollingInterval)

    // Return cleanup function
    return () => {
      disposed = true
      if (timeoutId) {
        clearTimeout(timeoutId)
        timeoutId = null
      }
    }
  }

  // Fetch subscription with proper trial expiration handling
  const fetchSubscription = async (email: string | undefined) => {
    if (!email) {
      onSubscriptionChange(false)
      onTrialChange(false, null)
      onLoadingChange(false, false)
      return
    }

    // Prevent duplicate subscription fetches
    if (fetchingSubscriptionRef.current) {
      return
    }

    fetchingSubscriptionRef.current = true

    try {
      // SECURITY: Always verify subscription status from database
      // localStorage is only used for performance optimization, not security decisions
      const storedSubscription = localStorage.getItem('hasSubscription')
      const storedTrial = localStorage.getItem('isTrial')
      const storedTrialEnd = localStorage.getItem('trialEndDate')
      const lastVerified = localStorage.getItem('lastSubscriptionVerified')

      // Only trust localStorage if verified recently (within 30 minutes) to prevent manipulation
      const VERIFICATION_WINDOW = 30 * 60 * 1000 // 30 minutes
      const now = Date.now()
      const lastVerifiedTime = lastVerified ? parseInt(lastVerified) : 0
      const isRecentlyVerified = (now - lastVerifiedTime) < VERIFICATION_WINDOW

      let canSkipDatabaseCheck = false

      if (isRecentlyVerified && storedSubscription === 'true') {
        onSubscriptionChange(true)
        canSkipDatabaseCheck = true
      }

      // Check stored trial with expiration and recent verification
      if (isRecentlyVerified && storedTrial === 'true' && storedTrialEnd) {
        const trialEnd = new Date(storedTrialEnd)
        const currentTime = new Date()

        if (trialEnd > currentTime) {
          onTrialChange(true, storedTrialEnd)
          canSkipDatabaseCheck = true
        } else {
          onTrialChange(false, null)
        }
      }

      // SECURITY: Periodically verify with database even with localStorage cache
      if (canSkipDatabaseCheck) {
        onLoadingChange(false, false)

        // Skip background verification to prevent infinite loops
        return
      }

      // Check for recent database failures to prevent retry loops
      const recentFailures = localStorage.getItem('dbFailureCount')
      const lastFailureTime = localStorage.getItem('lastDbFailure')
      const failureCount = recentFailures ? parseInt(recentFailures) : 0
      const lastFailure = lastFailureTime ? parseInt(lastFailureTime) : 0
      const timeSinceLastFailure = Date.now() - lastFailure

      // If we've had multiple failures recently, skip database check and use cache
      if (failureCount >= 3 && timeSinceLastFailure < 300000) { // 5 minutes
        onLoadingChange(false, false)
        return
      }

      // Query for both subscription and trial status with timeout and retry
      let data, error
      try {
        const result = await withRetry(async () => {
          const response = await supabase
            .from('profiles')
            .select('is_subscribed, is_trial, trial_end_date')
            .eq('email', email.toLowerCase())
            .single()
          return response
        })
        data = result.data
        error = result.error
      } catch (retryError) {
        error = retryError
        data = null
      }

      if (error) {
        // Track database failures to prevent retry loops
        const currentFailures = localStorage.getItem('dbFailureCount')
        const failureCount = currentFailures ? parseInt(currentFailures) + 1 : 1
        localStorage.setItem('dbFailureCount', failureCount.toString())
        localStorage.setItem('lastDbFailure', Date.now().toString())

        // CRITICAL: Always set loading to false on error to prevent infinite loading
        onLoadingChange(false, false)
        return
      }

      if (data) {
        // Reset failure tracking on successful database query
        localStorage.removeItem('dbFailureCount')
        localStorage.removeItem('lastDbFailure')

        // SECURITY: Update verification timestamp when we get fresh data from database
        localStorage.setItem('lastSubscriptionVerified', Date.now().toString())

        // If they're subscribed in DB, update immediately
        if (data.is_subscribed) {
          // Check if this is a NEW subscription (not in localStorage)
          const wasSubscribed = localStorage.getItem('hasSubscription') === 'true'
          const isNewSubscription = !wasSubscribed

          onSubscriptionChange(true)

          // Track conversion for new subscriptions
          trackConversionIfNew(email, isNewSubscription)

          onLoadingChange(false, false)
          return
        }

        // Check trial with expiration handling
        if (data.is_trial && data.trial_end_date) {
          const trialEnd = new Date(data.trial_end_date)
          const now = new Date()

          if (trialEnd > now) {
            // Active trial
            onTrialChange(true, data.trial_end_date)
            onLoadingChange(false, false)
            return
          } else {
            // Trial expired - update both local and database
            onTrialChange(false, null)
            // Update database to reflect expiration
            await updateTrialInDatabase(email, false, null)
          }
        } else {
          // No trial found
          onTrialChange(false, null)
        }

        // If no subscription and no valid trial, check if we need to start polling
        const hasActiveSubscription = data.is_subscribed
        const hasActiveTrial = data.is_trial && data.trial_end_date && new Date(data.trial_end_date) > new Date()

        if (!hasActiveSubscription && !hasActiveTrial) {
          // Clear any existing polling first
          if (pollingCleanupRef.current) {
            pollingCleanupRef.current()
          }

          // Start new polling and store cleanup function
          pollingCleanupRef.current = startPolling(email)
        } else {
          onLoadingChange(false, false)
        }
      } else {
        // No data returned from query
        onLoadingChange(false, false)
      }
    } catch (err) {
      onLoadingChange(false, false)
    } finally {
      // Reset the flag to allow future fetches
      fetchingSubscriptionRef.current = false
    }
  }

  // Cleanup function for external use
  const pollingCleanup = () => {
    if (pollingCleanupRef.current) {
      pollingCleanupRef.current()
      pollingCleanupRef.current = null
    }
  }

  return {
    fetchSubscription,
    pollingCleanup
  }
}
